import { StyleDeclarationValue } from 'aphrodite';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { CustomerImpersonationWarning } from '../../../web/components/CustomerImpersonationWarning';
import {
	CoffeeAppBarArea,
	CoffeeAppGrid,
	CoffeeNavArea,
	CoffeePageArea,
	CoffeeSubBarArea,
	CoffeeWarningArea,
} from '../../shell/CoffeeAppGrid';
import { useTysonMode } from '../../contexts/tysonMode';
import { CoffeeMobileRouteGuard, useIsCoffeeMobile } from '../../shell/CoffeeMobileRouteGuard';
import { styleSheet } from '../../shell/styles';
import { CoffeeAppBar } from '../CoffeeAppBar';
import { CoffeeNavBar, useIsCoffeeAppNavCompact } from '../CoffeeNavBar';

/**
 * Mirrors main lev's `AppLayout` in shape and in the dimensions it uses, but composes Coffee's own grid:
 * the sub-bar row is Coffee's alone, and main lev's `NavBar` calls `useTextMessaging()`, which throws in
 * the aida app.
 */
export const CoffeeAppLayout = ({
	children,
	subBar,
	pageRootStyles,
}: {
	children: React.ReactNode;
	/** Dialer + gamification. Omitted below `md`, where the grid row collapses to zero. */
	subBar?: React.ReactNode;
	pageRootStyles?: StyleDeclarationValue[];
}) => {
	const userSession = useUserSession();
	const showingCompactNav = useIsCoffeeAppNavCompact();
	const isMobile = useIsCoffeeMobile();
	const { isActive: isTysonModeActive } = useTysonMode();

	// Tyson mode repaints the page area, as the legacy `MainContainer` does on its own container.
	const pageStyles = isTysonModeActive ? [...(pageRootStyles ?? []), styleSheet.tysonBackground] : pageRootStyles;
	return (
		<CoffeeAppGrid>
			<CoffeeMobileRouteGuard />
			<CoffeeNavArea>
				<CoffeeNavBar compactLayout={showingCompactNav} />
			</CoffeeNavArea>
			<CoffeeWarningArea>
				{/* No `ManagedAccountAccessWarning` — main lev stacks one here, but Coffee has never shown it. */}
				<CustomerImpersonationWarning fixed={false} userSession={userSession} />
			</CoffeeWarningArea>
			<CoffeeAppBarArea>
				<CoffeeAppBar />
			</CoffeeAppBarArea>

			<CoffeeSubBarArea>{isMobile ? null : subBar}</CoffeeSubBarArea>
			<CoffeePageArea styles={pageStyles}>{children}</CoffeePageArea>
		</CoffeeAppGrid>
	);
};
